import { z } from 'zod';
import { IsoDateTime } from './common.js';

export const Email = z.string().trim().toLowerCase().pipe(z.email('Enter a valid email address.').max(254));

export const PASSWORD_MIN_LENGTH = 12;
export const NewPassword = z
  .string()
  .min(PASSWORD_MIN_LENGTH, `Use at least ${PASSWORD_MIN_LENGTH} characters.`)
  .max(256);

export const RegisterRequest = z.object({ email: Email, password: NewPassword });
export type RegisterRequest = z.input<typeof RegisterRequest>;

export const LoginRequest = z.object({ email: Email, password: z.string().min(1).max(256) });
export type LoginRequest = z.input<typeof LoginRequest>;

/** POST /auth/password: your other sessions are logged out; this one stays. */
export const ChangePasswordRequest = z.object({
  currentPassword: z.string().min(1).max(256),
  newPassword: NewPassword,
});
export type ChangePasswordRequest = z.input<typeof ChangePasswordRequest>;

export const User = z.object({
  id: z.uuid(),
  email: z.string(),
  createdAt: IsoDateTime,
});
export type User = z.infer<typeof User>;

/**
 * Returned by login, register and GET /auth/session. The dashboard sends csrfToken
 * back in the X-CSRF-Token header on every state-changing request.
 */
export const SessionResponse = z.object({ user: User, csrfToken: z.string() });
export type SessionResponse = z.infer<typeof SessionResponse>;

/** GET /auth/config, so the login page knows whether to offer sign-up. */
export const AuthConfigResponse = z.object({ registrationOpen: z.boolean() });
export type AuthConfigResponse = z.infer<typeof AuthConfigResponse>;

export const CSRF_HEADER = 'x-csrf-token';

// Two-factor login (TOTP, RFC 6238): 6 digits from an authenticator app, a new one
// every 30 seconds, plus one-time recovery codes for when the phone is lost.
export const TOTP_DIGITS = 6;
export const TOTP_PERIOD_S = 30;
export const RECOVERY_CODE_COUNT = 10;

/** After a correct password, when the account has two-factor login on. */
export const TwoFactorChallenge = z.object({
  twoFactorRequired: z.literal(true),
  /** Proves the password step passed; good for 5 minutes and 5 tries. */
  ticket: z.string(),
});
export type TwoFactorChallenge = z.infer<typeof TwoFactorChallenge>;

/** POST /auth/login answers with a session, or asks for a code first. */
export const LoginResponse = z.union([SessionResponse, TwoFactorChallenge]);
export type LoginResponse = z.infer<typeof LoginResponse>;

/** POST /auth/login/code: the authenticator code, or one of the recovery codes. */
export const LoginCodeRequest = z.object({
  ticket: z.string().min(20).max(100),
  code: z.string().trim().min(6).max(30),
});
export type LoginCodeRequest = z.input<typeof LoginCodeRequest>;

export const TotpCode = z
  .string()
  .trim()
  .regex(new RegExp(`^\\d{${TOTP_DIGITS}}$`), `Enter the ${TOTP_DIGITS}-digit code from your app.`);

/** POST /auth/2fa/setup: the secret to put in the authenticator app (not yet switched on). */
export const TwoFactorSetupResponse = z.object({
  /** Base32, for typing in by hand. */
  secret: z.string(),
  /** otpauth:// link, shown as a QR code. */
  otpauthUrl: z.string(),
});
export type TwoFactorSetupResponse = z.infer<typeof TwoFactorSetupResponse>;

export const EnableTwoFactorRequest = z.object({ code: TotpCode });
export type EnableTwoFactorRequest = z.input<typeof EnableTwoFactorRequest>;

/** Shown once, when two-factor login is switched on or the codes are replaced. */
export const RecoveryCodesResponse = z.object({ recoveryCodes: z.array(z.string()) });
export type RecoveryCodesResponse = z.infer<typeof RecoveryCodesResponse>;

/** Turning it off needs both factors: the password and a code (or a recovery code). */
export const DisableTwoFactorRequest = z.object({
  password: z.string().min(1).max(256),
  code: z.string().trim().min(6).max(30),
});
export type DisableTwoFactorRequest = z.input<typeof DisableTwoFactorRequest>;
